/**
 * Workspace selection — Field or Lab.
 *
 * A WORKSPACE IS NOT A PERMISSION.
 *
 * It is a statement of physical situation: "I am in a field holding a phone",
 * or "I am in the lab, at a bench or a desk". The UI adapts to that and grants
 * nothing. Every route stays gated by the same role checks it had
 * before, on both sides of the API.
 *
 * This matters because a workspace lives in localStorage, and a security
 * control that lives in localStorage is not a security control. The rule for
 * anyone extending this: **no backend route may ever read the workspace.** It
 * is never a JWT claim and never an authorisation input.
 *
 * A workspace is also never a trap. Every mode carries a one-click switcher,
 * so a collector who picked Field and now needs the lab hub does not
 * have to log out. That is the usual failure of designs like this one and it
 * is avoidable by construction.
 */

const STORAGE_KEY = 'mb_workspace';

export const WORKSPACES = ['field', 'lab'];

// Lab is Home: the isolates hub (pages/Dashboard.js) with the full system's
// menus around it, and the one way into lab work. The bench scan screen it
// used to open is still /lab, reached by a deep link.
export const WORKSPACE_ROUTES = {
  field: '/field',
  lab: '/',
};

// Which roles open which workspace.
//
// `lab` is the full app, so it is open to every staff role including
// read_only and collector: it took over the old Data Analysis set, and nobody
// who can log in should lose the desk app they could already reach. `field`
// leaves out read_only -- Field Mode exists to CREATE records -- and keeps
// `collector`, whose entire job is the thing Field Mode is for. The Phase 6
// roles (authorized_researcher, iso_auditor, system_admin) open neither and
// land on Home without being asked (landingRedirect). An external researcher
// (0105) records their own observations and reads nothing of the lab's, so
// Field is theirs and Lab is not: they land in Field, in Observation mode.
const ROLE_ACCESS = {
  field: ['collection_manager', 'internal_expert', 'lab_technician',
          'student', 'lab_manager', 'collector', 'external_researcher'],
  lab: ['collection_manager', 'internal_expert', 'lab_technician',
        'student', 'lab_manager', 'read_only', 'collector'],
};

/**
 * Workspaces this user's roles open, in display order.
 *
 * A workspace the user cannot use is omitted rather than shown disabled: a
 * greyed-out card teaches someone only that something exists which they
 * cannot have.
 */
export function availableWorkspaces(user) {
  const roles = user?.roles || [];
  // `public`-role accounts never reach the staff app at all — PrivateRoute
  // sends them to /survey — so they get nothing here either.
  if (roles.includes('public')) return [];
  return WORKSPACES.filter(w => ROLE_ACCESS[w].some(r => roles.includes(r)));
}

export function canUseWorkspace(user, workspace) {
  return availableWorkspaces(user).includes(workspace);
}

/**
 * The stored choice, or null.
 *
 * Reads defensively: localStorage throws outright in some privacy modes, and
 * a stored value can be stale nonsense after a role change. An unusable value
 * is treated as no value -- including 'analysis', a workspace that no longer
 * exists (its desk app is Lab now), so whoever had it is asked again.
 */
export function getWorkspace(user) {
  let stored = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!stored || !WORKSPACES.includes(stored)) return null;
  if (user && !canUseWorkspace(user, stored)) return null;
  return stored;
}

export function setWorkspace(workspace) {
  if (!WORKSPACES.includes(workspace)) return false;
  try {
    localStorage.setItem(STORAGE_KEY, workspace);
    return true;
  } catch {
    // A per-device convenience that failed to save is not an error worth
    // interrupting anyone over; the selector simply asks again next time.
    return false;
  }
}

export function clearWorkspace() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Where a navigation should be sent instead, or null to let it through.
 *
 * Only a bare entry is ever redirected, and only when no choice is stored. A
 * DEEP LINK ALWAYS WINS: a user following a link to /plates (a code lookup, a
 * hub row) goes to /plates, and so does Home with a query (the hub keeps its
 * view in the URL, and ?intake= opens an intake record there). Getting this
 * wrong would break linkedRecordNav.js and every deep link in the app, which
 * is a far worse outcome than someone occasionally not being asked.
 *
 * With nothing stored: two workspaces are a choice (/workspace); one is not,
 * so its route is taken when it is not Home already; none (the Phase 6 roles)
 * lands on Home and is never shown a chooser with nothing on it.
 *
 * Signing in clears the stored choice (pages/Login.js), so the chooser comes
 * right after every sign-in; a reload or a restored session does not.
 */
export function landingRedirect(user, location) {
  if (!user) return null;
  if ((location?.pathname || '/') !== '/' || location?.search) return null;
  if (getWorkspace(user)) return null;
  const available = availableWorkspaces(user);
  if (available.length > 1) return '/workspace';
  if (available.length === 1 && WORKSPACE_ROUTES[available[0]] !== '/') {
    return WORKSPACE_ROUTES[available[0]];
  }
  return null;
}

/** Should this navigation be interrupted to ask which workspace? */
export function shouldPromptForWorkspace(user, pathname) {
  return landingRedirect(user, { pathname }) === '/workspace';
}
